
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import menuService from '../services/menuService';

const MenuContext = createContext(null);

export function MenuProvider({ children }) {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch menu from backend
  const fetchMenu = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await menuService.getAll();
      setMenuItems(data);
    } catch (e) {
      setError(e.message || 'Failed to fetch menu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  // Add menu item to backend
  const addMenuItem = async (item) => {
    const newItem = await menuService.create(item);
    setMenuItems((prev) => [...prev, newItem]);
    return newItem;
  };

  // Update menu item in backend
  const updateMenuItem = async (id, data) => {
    const updated = await menuService.update(id, data);
    setMenuItems((prev) => prev.map((it) => (it.id === id ? updated : it)));
    return updated;
  };

  // Delete menu item in backend
  const deleteMenuItem = async (id) => {
    await menuService.remove(id);
    setMenuItems((prev) => prev.filter((it) => it.id !== id));
  };

  // Toggle availability in backend
  const toggleAvailability = async (id) => {
    const item = menuItems.find((it) => it.id === id);
    if (!item) return;
    return updateMenuItem(id, { ...item, available: !item.available });
  };

  // Replace all menu items (not used, but for completeness)
  const setAllMenu = (items) => setMenuItems(items);

  // Clear all menu items (not used, but for completeness)
  const clearAllMenu = async () => {
    // Optionally, you could add a backend endpoint to delete all
    setMenuItems([]);
  };

  return (
    <MenuContext.Provider value={{ menuItems, loading, error, fetchMenu, addMenuItem, updateMenuItem, deleteMenuItem, toggleAvailability, setAllMenu, clearAllMenu }}>
      {children}
    </MenuContext.Provider>
  );
}

export const useMenu = () => {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('useMenu must be used within a MenuProvider');
  return ctx;
};
